<template>
  <div class="automation-file-input">
    <template v-if="multiple">
      <div class="attachment-list">
        <div
          v-for="(attachment, index) in attachments"
          :key="`${attachment.blobId}-${index}`"
          class="attachment-bar"
        >
          <div class="attachment-bar__content">
            <fluent-icon
              icon="checkmark-circle"
              type="outline"
              size="16"
              class="success-icon"
            />
            <span
              v-tooltip="attachment.filename"
              class="attachment-bar__filename"
            >
              {{ attachment.filename || $t('AUTOMATION.ATTACHMENT.LABEL_UPLOADED') }}
            </span>
          </div>
          <button
            v-if="allowRemove"
            type="button"
            class="attachment-bar__remove"
            @click="removeAttachmentAt(index)"
          >
            {{ $t('AUTOMATION.ATTACHMENT.REMOVE') }}
          </button>
        </div>

        <label
          v-if="uploadState !== 'processing'"
          class="input-wrapper input-wrapper--bar"
        >
          <input type="file" name="attachment" multiple @change="onChangeFile" />
          <fluent-icon icon="file-upload" />
          <p class="file-button">{{ uploadLabel }}</p>
        </label>
        <div v-else class="input-wrapper input-wrapper--bar processing">
          <spinner />
          <p class="file-button">{{ label }}</p>
        </div>
      </div>
    </template>

    <template v-else>
      <label
        v-if="variant !== 'bar' || uploadState !== 'uploaded'"
        class="input-wrapper"
        :class="[uploadState, variant === 'bar' ? 'input-wrapper--bar' : '']"
      >
        <input
          v-if="uploadState !== 'processing'"
          type="file"
          name="attachment"
          :class="uploadState === 'processing' ? 'disabled' : ''"
          @change="onChangeFile"
        />
        <spinner v-if="uploadState === 'processing'" />
        <fluent-icon v-if="uploadState === 'idle'" icon="file-upload" />
        <fluent-icon
          v-if="uploadState === 'failed'"
          icon="dismiss-circle"
          type="outline"
          class="error-icon"
        />
        <p class="file-button">{{ label }}</p>
      </label>

      <div v-else class="attachment-bar">
        <div class="attachment-bar__content">
          <fluent-icon
            icon="checkmark-circle"
            type="outline"
            size="16"
            class="success-icon"
          />
          <span v-tooltip="displayFileName" class="attachment-bar__filename">
            {{ displayFileName }}
          </span>
        </div>
        <button
          v-if="allowRemove"
          type="button"
          class="attachment-bar__remove"
          @click="removeAttachment"
        >
          {{ $t('AUTOMATION.ATTACHMENT.REMOVE') }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import alertMixin from 'shared/mixins/alertMixin';
export default {
  components: {
    Spinner,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    initialFileName: {
      type: String,
      default: '',
    },
    initialAttachments: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: String,
      default: 'default',
    },
    allowRemove: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      uploadState: 'idle',
      label: this.$t('AUTOMATION.ATTACHMENT.LABEL_IDLE'),
      fileName: this.initialFileName || '',
      attachments: [],
    };
  },
  computed: {
    displayFileName() {
      return this.fileName || this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOADED');
    },
    uploadLabel() {
      if (this.attachments.length) {
        return this.$t('AUTOMATION.ATTACHMENT.ADD_MORE');
      }
      return this.$t('AUTOMATION.ATTACHMENT.LABEL_IDLE');
    },
  },
  watch: {
    initialFileName(newName) {
      if (this.multiple) return;
      if (newName) {
        this.fileName = newName;
        this.uploadState = 'uploaded';
      }
    },
    initialAttachments: {
      deep: true,
      handler() {
        if (this.multiple) {
          this.syncAttachmentsFromValue();
        }
      },
    },
    value: {
      immediate: true,
      handler(blobIds) {
        if (this.multiple) {
          this.syncAttachmentsFromValue(blobIds);
          return;
        }

        if (!blobIds?.length && this.uploadState === 'uploaded' && !this.fileName) {
          this.resetUploadState();
        }
      },
    },
  },
  mounted() {
    if (this.multiple) {
      this.syncAttachmentsFromValue();
      return;
    }

    if (this.initialFileName) {
      this.fileName = this.initialFileName;
      this.uploadState = 'uploaded';
    }
  },
  methods: {
    syncAttachmentsFromValue(blobIds = this.value) {
      const ids = Array.isArray(blobIds) ? blobIds.filter(Boolean) : [];
      this.attachments = ids.map(blobId => {
        const existing = this.attachments.find(
          attachment => String(attachment.blobId) === String(blobId)
        );
        const initial = this.initialAttachments.find(
          attachment => String(attachment.blobId) === String(blobId)
        );
        return {
          blobId,
          filename: existing?.filename || initial?.filename || '',
        };
      });
    },
    resetUploadState() {
      this.uploadState = 'idle';
      this.fileName = '';
      this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_IDLE');
    },
    removeAttachment() {
      this.resetUploadState();
      this.$emit('input', []);
    },
    removeAttachmentAt(index) {
      this.attachments.splice(index, 1);
      this.$emit(
        'input',
        this.attachments.map(attachment => attachment.blobId)
      );
    },
    async uploadFile(file) {
      const formData = new FormData();
      formData.append('attachment', file, file.name);
      return this.$store.dispatch('automations/uploadAttachment', formData);
    },
    async onChangeFile(event) {
      const files = this.multiple
        ? Array.from(event.target.files || [])
        : [event.target.files?.[0]].filter(Boolean);
      if (!files.length) return;

      if (this.multiple) {
        this.uploadState = 'processing';
        this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOADING');
        try {
          const uploadedAttachments = [...this.attachments];
          for (const file of files) {
            const blobId = await this.uploadFile(file);
            uploadedAttachments.push({ blobId, filename: file.name });
          }
          this.attachments = uploadedAttachments;
          this.$emit(
            'input',
            uploadedAttachments.map(attachment => attachment.blobId)
          );
          this.uploadState = 'idle';
        } catch (error) {
          this.uploadState = 'failed';
          this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOAD_FAILED');
          this.showAlert(this.$t('AUTOMATION.ATTACHMENT.UPLOAD_ERROR'));
        } finally {
          event.target.value = '';
        }
        return;
      }

      const file = files[0];
      this.uploadState = 'processing';
      this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOADING');
      try {
        const id = await this.uploadFile(file);
        this.fileName = file.name;
        this.$emit('input', [id]);
        this.uploadState = 'uploaded';
        this.label = file.name;
      } catch (error) {
        this.uploadState = 'failed';
        this.label = this.$t('AUTOMATION.ATTACHMENT.LABEL_UPLOAD_FAILED');
        this.showAlert(this.$t('AUTOMATION.ATTACHMENT.UPLOAD_ERROR'));
      } finally {
        event.target.value = '';
      }
    },
  },
};
</script>

<style scoped>
.automation-file-input {
  @apply w-full;
}

.attachment-list {
  @apply flex w-full flex-col gap-2;
}

input[type='file'] {
  @apply hidden;
}
.input-wrapper {
  @apply flex h-9 bg-white dark:bg-slate-900 py-1 px-2 items-center text-xs cursor-pointer rounded-sm border border-dashed border-woot-100 dark:border-woot-500;
}
.input-wrapper--bar {
  @apply w-full h-10;
}
.success-icon {
  @apply text-green-500 dark:text-green-600 mr-2 shrink-0;
}
.error-icon {
  @apply text-red-500 dark:text-red-600 mr-2;
}

.processing {
  @apply cursor-not-allowed opacity-90;
}
.file-button {
  @apply whitespace-nowrap overflow-hidden text-ellipsis w-full mb-0;
}

.attachment-bar {
  @apply flex h-10 w-full items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-slate-600
    bg-white dark:bg-slate-900 px-3;
}

.attachment-bar__content {
  @apply flex min-w-0 flex-1 items-center gap-2;
}

.attachment-bar__filename {
  @apply text-sm text-slate-700 dark:text-slate-200 truncate;
}

.attachment-bar__remove {
  @apply shrink-0 text-sm text-woot-500 hover:text-woot-600 dark:text-woot-400 dark:hover:text-woot-300
    bg-transparent border-0 cursor-pointer p-0;
}
</style>
